import { useEffect, useRef } from 'react';

type AmbientBubble = {
  x: number;
  y: number;
  r: number;
  vy: number;
  wobble: number;
};

type AmbientBubblesProps = {
  active: boolean;
  density?: number;
};

/** Slow ambient bubbles behind header / live dashboard. */
export function AmbientBubbles({ active, density = 10 }: AmbientBubblesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let width = 0;
    let height = 0;
    let bubbles: AmbientBubble[] = [];
    let visible = !document.hidden;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      bubbles = Array.from({ length: density }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: 3 + Math.random() * 10,
        vy: 0.15 + Math.random() * 0.35,
        wobble: Math.random() * Math.PI * 2,
      }));
    };

    const onVisibility = () => {
      visible = !document.hidden;
    };

    resize();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);

    const draw = () => {
      if (visible) {
        ctx.clearRect(0, 0, width, height);
        for (const b of bubbles) {
          b.y -= b.vy;
          b.wobble += 0.008;
          b.x += Math.sin(b.wobble) * 0.15;
          if (b.y + b.r < 0) {
            b.y = height + b.r;
            b.x = Math.random() * width;
          }
          ctx.beginPath();
          ctx.fillStyle = 'rgba(198, 241, 53, 0.06)';
          ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, density]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full opacity-80"
      aria-hidden
    />
  );
}
